import React from 'react'
import { create } from 'jss'
import rtl from 'jss-rtl'
import { BrowserRouter as Router } from 'react-router-dom'
import { createGenerateClassName, createMuiTheme } from '@material-ui/core/styles'
import { StylesProvider, ThemeProvider as MuiThemeProvider, jssPreset } from '@material-ui/styles'
import { LocalStorageService } from './Services/LocalStorageService'
import Configuration from './Services/Api/Configuration'

// Configure JSS
const jss = create({ plugins: [...jssPreset().plugins, rtl()] })
const generateClassName = createGenerateClassName()
const { DefaultFont, DefaultDirection } = Configuration

const ThemeProvider = ({ children }) => {
  const [font, setFont] = React.useState(DefaultFont)
  const [storageDirection, setStorageDirection] = React.useState(DefaultDirection)

  let isSubscribed = false
  React.useEffect(() => {
    // eslint-disable-next-line
    isSubscribed = true
    LocalStorageService.get('ETaxAdminAppFontName').then((storageFont) => {
      if (isSubscribed) {
        setFont(storageFont)
      }
    })
    LocalStorageService.get('ETaxAdminAppDirection').then((AppDirection) => {
      if (isSubscribed) {
        setStorageDirection(AppDirection)
      }
    })
    return () => isSubscribed = false
  }, [])


  let defaultTheme = createMuiTheme()
  let theme = createMuiTheme({
    direction: storageDirection,
    palette: {
      primary: {
        light: '#438fce',
        hover: '#0062cc',
        main: '#438fce',
        darker: '#0A2F95',
        dark: '#00258c',
        contrastText: '#000'
      },
      secondary: {
        light: '#edf6fe',
        main: '#cfe6fc',
        dark: '#adcbe3',
        contrastText: '#000'
      },
      error: {
        light: '#fff2f4',
        main: '#dc3545',
        dark: '#c32130',
        contrastText: '#000'
      },
      success: {
        main: '#28a745',
        light: '#20c997'
      },
      info: {
        main: '#17a2b8',
        light: '#edf7ff'
      },
      warning: {
        light: '#fff9ec',
        main: '#ffc107',
        dark: '#FF8412'
      },
      text: {
        primary: '#171725',
        secondary: '#92929d',
        hint: '#98a9bc'
      }
    },
    colors: {
      grey: '#767676',
    },
    typography: {
      'fontFamily': font,
      'htmlFontSize': 16,
      'fontSize': 14,
      'fontWeightLight': 300,
      'fontWeightRegular': 500,
      'fontWeightBold': 900
    },
    customShadows: {
      widget:
        '0px 3px 11px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A',
      widgetDark:
        '0px 3px 18px 0px #4558A3B3, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A',
      widgetWide:
        '0px 12px 33px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A'
    },
    overrides: {
      MuiBackdrop: {
        root: {
          backgroundColor: '#4A4A4A1A'
        }
      },
      MuiButton: {
        outlinedPrimary: {
          '&:hover': {
            backgroundColor: '#fff',
          }
        },
        textPrimary: {
          '&:hover': {
            backgroundColor: 'transparent',
          }
        }
      },
      MuiMenu: {
        paper: {
          boxShadow:
            '0px 3px 11px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A'
        }
      },
      MuiSelect: {
        icon: {
          color: '#B9B9B9'
        }
      },
      MuiListItem: {
        root: {
          '&$selected': {
            backgroundColor: '#F3F5FF !important',
            '&:focus': {
              backgroundColor: '#F3F5FF'
            }
          }
        },
        button: {
          '&:hover, &:focus': {
            backgroundColor: '#F3F5FF'
          }
        }
      },
      MuiTouchRipple: {
        child: {
          backgroundColor: 'white'
        }
      },
      MuiTableRow: {
        root: {
          height: 56
        }
      },
      MuiTableCell: {
        root: {
          borderBottom: '1px solid rgba(224, 224, 224, .5)'
        },
        head: {
          fontSize: '0.95rem'
        },
        body: {
          fontSize: '0.95rem'
        }
      },
      MUIDataTableSelectCell: {
        icon: {
          transform: defaultTheme.direction === 'ltr' ? 'scaleX(1)' : 'scaleX(-1)'
        },
        expanded: {
          transform: defaultTheme.direction === 'ltr' ? 'scaleX(1) rotate(-90deg)' : 'scaleX(-1) rotate(-90deg)'
        }
      },
      MUIDataTable: {
        responsiveBase: {
          '@media print': {
            overflow: 'hidden'
          }
        }
      },
      MUIDataTableToolbar: {
        actions: {
          display: 'flex',
          flex: 'initial',
          // move all icons to the right
          '& > span, & > button': {
            order: 99
          },
          // target the custom toolbar icon
          '& > span.custom-toolbar-btn, & > button.custom-toolbar-btn': {
            order: 1
          },
          [defaultTheme.breakpoints.down('sm')]: {
            justifyContent: 'center'
          }
        },
        filterPaper: {
          width: '50%',
          [defaultTheme.breakpoints.down('sm')]: {
            width: 'calc(100% - 32px)',
            maxWidth: 'calc(100% - 32px)'
          },
          '& > button': {
            left: 'auto !important',
            right: '4px !important'
          }
        },
        filterCloseIcon: {
          left: 'auto !important',
          right: '4px !important'
        }
      }
    }
  })

  return (
    <StylesProvider jss={jss} generateClassName={generateClassName}>
      <MuiThemeProvider theme={theme}>
        <Router>
          {children}
        </Router>
      </MuiThemeProvider>
    </StylesProvider>
  )
}

export default ThemeProvider
